// @flow
import { canSaveSearch } from '@seek/seek-refine-job-search';
import { UPDATE_CRITERIA } from 'src/store/search/search';
import createApiClient from 'src/modules/seek-jobs-api-client';
import isSearchSaved from './is-search-saved';
import { get } from 'lodash';

const api = createApiClient();

export const SAVE_SEARCH_SUCCESS = 'SAVE_SEARCH_SUCCESS';
export const UPDATE_LAST_EXECUTION_DATE_SUCCESS =
  'UPDATE_LAST_EXECUTION_DATE_SUCCESS';
export const UPDATE_LAST_EXECUTION_DATE_ERROR =
  'UPDATE_LAST_EXECUTION_DATE_ERROR';

const SAVE_SEARCH_BEGIN = 'SAVE_SEARCH_BEGIN';
const SAVE_SEARCH_ERROR = 'SAVE_SEARCH_ERROR';
const SAVE_SEARCH_EXCEEDED = 'SAVE_SEARCH_EXCEEDED';
const FETCH_SAVED_SEARCHES_SUCCESS = 'FETCH_SAVED_SEARCHES_SUCCESS';
const FETCH_SAVED_SEARCHES_ERROR = 'FETCH_SAVED_SEARCHES_ERROR';
const UPDATE_IS_SAVED_SEARCH = 'UPDATE_IS_SAVED_SEARCH';

const JOB_MAIL_BEGIN = 'JOB_MAIL_BEGIN';
export const JOB_MAIL_SUCCESS = 'JOB_MAIL_SUCCESS';
const JOB_MAIL_ERROR = 'JOB_MAIL_ERROR';
const JOBMAIL_EMAIL_CHANGE = 'JOBMAIL_EMAIL_CHANGE';

export const states = {
  SAVING: 'SAVING',
  SAVED: 'SAVED',
  UNSAVED: 'UNSAVED',
  ERROR: 'ERROR',
  EXCEEDED: 'EXCEEDED'
};

const initialState = {
  emailAddress: '',
  errorMessage: '',
  saveable: true,
  status: states.UNSAVED,
  title: ''
};

export default function reducer(
  state: Object = initialState,
  action: any = {}
) {
  switch (action.type) {
    case UPDATE_CRITERIA: {
      const { lastQuery, query } = action.payload;
      const { status } = state;

      const hasSavedSearch = status === states.SAVED;
      const saveable = canSaveSearch(lastQuery, query, hasSavedSearch);

      return {
        ...state,
        status: saveable ? states.UNSAVED : status,
        saveable,
        errorMessage: '',
        title: ''
      };
    }

    case SAVE_SEARCH_BEGIN: {
      return {
        ...state,
        status: states.SAVING
      };
    }

    case UPDATE_IS_SAVED_SEARCH: {
      const { query, locale } = action.payload;

      if (!state.savedSearches) {
        return state;
      }

      return {
        ...state,
        isSaved: isSearchSaved(query, state.savedSearches, locale)
      };
    }

    case SAVE_SEARCH_SUCCESS: {
      const { title, query } = action.payload;

      return {
        ...state,
        title,
        status: states.SAVED,
        isSaved: true,
        savedSearches: [...get(state, 'savedSearches'), { query }]
      };
    }

    case SAVE_SEARCH_ERROR: {
      return {
        ...state,
        status: states.ERROR
      };
    }

    case FETCH_SAVED_SEARCHES_SUCCESS: {
      return {
        ...state,
        savedSearches: action.payload
      };
    }

    case SAVE_SEARCH_EXCEEDED: {
      return {
        ...state,
        status: states.EXCEEDED
      };
    }

    case JOBMAIL_EMAIL_CHANGE: {
      return {
        ...state,
        status: states.UNSAVED,
        errorMessage: ''
      };
    }

    case JOB_MAIL_BEGIN: {
      return {
        ...state,
        status: states.SAVING
      };
    }

    case JOB_MAIL_SUCCESS: {
      const { emailAddress } = action.payload;
      return {
        ...state,
        status: states.SAVED,
        errorMessage: '',
        emailAddress
      };
    }

    case JOB_MAIL_ERROR: {
      return {
        ...state,
        status: states.ERROR,
        errorMessage: 'Something went wrong'
      };
    }

    default: {
      return state;
    }
  }
}

export const save = ({
  locale,
  query,
  location,
  saveSearchPosition
}: {
  locale: Locale,
  query: SearchParams,
  location: Location,
  saveSearchPosition: number
}) => (dispatch: Function) => {
  dispatch({
    type: SAVE_SEARCH_BEGIN,
    meta: { hotjar: 'Saved Search' }
  });

  return api.candidate
    .saveSearch({ locale, query, location })
    .then(({ title }) => {
      dispatch({
        type: SAVE_SEARCH_SUCCESS,
        payload: {
          title,
          query,
          saveSearchPosition
        }
      });
    })
    .catch(err => {
      if (get(err, 'response.status') === 422) {
        dispatch({ type: SAVE_SEARCH_EXCEEDED });
      } else {
        dispatch({ type: SAVE_SEARCH_ERROR, error: true, payload: err });
      }
    });
};

export const emailChange = () => ({ type: JOBMAIL_EMAIL_CHANGE });
export const updateIsSavedSearch = (query: SearchParams) => (
  dispatch: Function,
  getState: Function
) => {
  const { locale } = getState().location;
  dispatch({ type: UPDATE_IS_SAVED_SEARCH, payload: { query, locale } });
};

export const updateLastExecutionDate = (id: string) => (
  dispatch: Function,
  getState: Function
) => {
  const lastExecutionDateUtc = new Date().toUTCString();
  const {
    location: { locale }
  } = getState();
  return api.savedSearches
    .updateSavedSearchLastExecutionDate({
      id,
      lastExecutionDateUtc,
      locale
    })
    .then(() =>
      dispatch({
        type: UPDATE_LAST_EXECUTION_DATE_SUCCESS,
        metrics: {
          name: UPDATE_LAST_EXECUTION_DATE_SUCCESS
        }
      })
    )
    .catch(() =>
      dispatch({
        type: UPDATE_LAST_EXECUTION_DATE_ERROR,
        metrics: {
          name: UPDATE_LAST_EXECUTION_DATE_ERROR
        }
      })
    );
};

export const fetchSavedSearches = (locale: Locale) => (
  dispatch: Function,
  getState: Function
) => {
  const {
    search: { query }
  } = getState();

  return api.candidate
    .getSavedSearches(locale)
    .then(({ data }) => {
      dispatch({
        type: FETCH_SAVED_SEARCHES_SUCCESS,
        payload: data
      });

      dispatch(updateIsSavedSearch(query));
    })
    .catch(err => {
      dispatch({ type: FETCH_SAVED_SEARCHES_ERROR, error: true, payload: err });
    });
};

export const jobMail = ({
  locale,
  query,
  location,
  emailAddress,
  jobMailType
}: {
  locale: Locale,
  query: SearchParams,
  location: Location,
  emailAddress: string,
  jobMailType: string
}) => (dispatch: Function) => {
  dispatch({ type: JOB_MAIL_BEGIN });

  return api.candidate
    .jobMail({ locale, query, location, emailAddress })
    .then(() => {
      dispatch({
        type: JOB_MAIL_SUCCESS,
        meta: { hotjar: 'Job Mail Created', jobMailType },
        payload: { emailAddress, query }
      });
    })
    .catch(err => {
      dispatch({
        type: JOB_MAIL_ERROR,
        error: true,
        payload: err,
        meta: { hotjar: 'Job Mail Error' }
      });
    });
};
